import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { getTableActionsConfig } from '../../config/appConfig';
import { ArrowUpIcon, FileIcon } from '../ui/Icons';
import { CELL_RENDERERS } from './tableCellRenderers';

// A card-based alternative "table" that uses the same columns/data model
// as AdvancedTable, but renders rows as responsive cards.
// Keeps theme styling and adds simple multi-column sorting.

const DEFAULT_CARD_WIDTH = 'min-w-[280px]';

const CardTable = ({
	columns = [],
	data = [],
	className,
	tableId = 'card-table',
	onRowClick,
	cardClassName,
	actionsRenderer,
	controls = true,
	onFileCountClick
}) => {
	const { theme } = useTheme();
	
	// Get table actions configuration
	const actionsVisibility = getTableActionsConfig('visibility');
	const spacing = getTableActionsConfig('spacing');
	const [sortConfig, setSortConfig] = useState([]); // [{key, direction, priority}]

	// Load saved config
	useEffect(() => {
		const saved = localStorage.getItem(`cardtable-config-${tableId}`);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				if (Array.isArray(parsed.sortConfig)) setSortConfig(parsed.sortConfig);
			} catch {}
		}
	}, [tableId]);

	// Save config
	useEffect(() => {
		const payload = { sortConfig };
		localStorage.setItem(`cardtable-config-${tableId}`, JSON.stringify(payload));
	}, [tableId, sortConfig]);

	const handleSort = useCallback((columnKey, event) => {
		const isMulti = event && (event.ctrlKey || event.metaKey);
		setSortConfig(prev => {
			if (!isMulti) {
				const existing = prev.find(s => s.key === columnKey);
				if (existing) {
					return existing.direction === 'asc' ? [{ key: columnKey, direction: 'desc', priority: 1 }] : [];
				}
				return [{ key: columnKey, direction: 'asc', priority: 1 }];
			}
			const idx = prev.findIndex(s => s.key === columnKey);
			if (idx >= 0) {
				const existing = prev[idx];
				if (existing.direction === 'asc') {
					const copy = [...prev];
					copy[idx] = { ...existing, direction: 'desc' };
					return copy;
				}
				return prev.filter(s => s.key !== columnKey).map((s, i) => ({ ...s, priority: i + 1 }));
			}
			return [...prev, { key: columnKey, direction: 'asc', priority: prev.length + 1 }];
		});
	}, []);

	const sortedData = useMemo(() => {
		if (sortConfig.length === 0) return data;
		return [...data].sort((a, b) => {
			for (const sort of sortConfig.sort((a, b) => a.priority - b.priority)) {
				const aVal = a[sort.key];
				const bVal = b[sort.key];
				let cmp = 0;
				if (aVal == null && bVal == null) continue;
				if (aVal == null) return 1;
				if (bVal == null) return -1;
				if (aVal instanceof Date && bVal instanceof Date) cmp = aVal.getTime() - bVal.getTime();
				else if (typeof aVal === 'number' && typeof bVal === 'number') cmp = aVal - bVal;
				else cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
				if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
			}
			return 0;
		});
	}, [data, sortConfig]);

	return (
		<div className={cn(
			"relative h-full max-h-full",
			theme.colors.surface,
			className
		)}>
			{/* Controls */}
			{controls && (
				<div className={cn(
					"sticky top-0 z-10 flex items-center gap-2 px-3 py-2 border-b backdrop-blur",
					theme.colors.surface,
					theme.border.light
				)}>
					<span className={cn("text-xs font-semibold uppercase tracking-wider", theme.text.secondary)}>Sort</span>
					<div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
						{columns.filter(c => c.key !== 'actions').map(col => {
							const active = sortConfig.find(s => s.key === col.key);
							return (
								<button
									key={col.key}
									onClick={(e) => handleSort(col.key, e)}
									onMouseDown={(e) => e.preventDefault()}
									className={cn(
										"inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs border transition-colors",
										active ? theme.colors.primary + ' ' + theme.text.inverse : theme.border.light
									)}
									title={`Sort by ${col.header || col.key}`}
								>
									<span className="truncate max-w-[180px]">{col.header || col.key}</span>
									{active && (
										<ArrowUpIcon className={cn("w-3 h-3", active.direction === 'desc' && 'rotate-180')} />
									)}
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Cards grid */}
			<div className={cn(
				"p-3 grid gap-3",
				"sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
			)}>
				{sortedData.map((row, idx) => (
					<div
						key={idx}
						onClick={() => onRowClick && onRowClick(row)}
						className={cn(
							"group rounded-xl border transition-all duration-200 hover:-translate-y-[1px]",
							theme.border.light,
							theme.shadow.md,
							theme.colors.surface,
							DEFAULT_CARD_WIDTH,
							cardClassName
						)}
					>
						{/* Card header: first two columns */}
						<div className={cn("px-4 py-3 border-b", theme.border.light)}>
							<div className={cn("text-sm font-semibold truncate", theme.text.primary)}>
								{String(row[columns[0]?.key] ?? '')}
							</div>
							{columns[1] && (
								<div className={cn("text-xs truncate mt-1", theme.text.secondary)}>
									{String(row[columns[1].key] ?? '')}
								</div>
							)}
						</div>

						{/* Card body: rest of columns except actions */}
						<div className="px-4 py-3 grid grid-cols-2 gap-3">
							{(() => {
								// For modules with only 2 columns, show the second column (index 1) in the body
								// For modules with more columns, skip the first 2 (header/title area)
								const startIndex = columns.length <= 2 ? 1 : 2;
								const filteredColumns = columns.filter((c, i) => c.key !== 'actions' && i >= startIndex);
								return filteredColumns;
							})().map(col => (
									<div key={col.key} className="min-w-0">
										<div className={cn("text-[10px] uppercase tracking-wider mb-1", theme.text.muted)}>
											{col.header || col.label || col.key}
										</div>
										<div className={cn("text-sm truncate", theme.text.primary)}>
											{(() => {
												const v = row[col.key];
												if (col.key === 'completed') {
													console.log('COMPLETED FIELD:', { key: col.key, value: v, cellRenderer: col.cellRenderer, toggleConfig: col.toggleConfig });
												}
												if (v == null || v === '') return <span className={theme.text.muted}>—</span>;
												if (col.cellRenderer === 'date') {
													try { const d = new Date(v); return d.toLocaleDateString(); } catch { return String(v); }
												}
												if (col.cellRenderer === 'booleanBadge') {
													return (
														<span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium",
															v ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
														)}>
															{v ? 'Yes' : 'No'}
														</span>
													);
												}
												if (col.cellRenderer === 'toggleSwitch') {
													const config = col.toggleConfig || {};
													const handleToggle = async () => {
														if (config.onToggle && window.todoHandlers && window.todoHandlers[config.onToggle]) {
															try {
																await window.todoHandlers[config.onToggle](row.id, v);
																// The handler manages the API call and data refresh
															} catch (error) {
																console.error('Toggle failed:', error);
															}
														}
													};
													
													return (
														<button
															onClick={handleToggle}
															className={cn(
																"relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
																v ? 'bg-green-500' : 'bg-gray-300'
															)}
														>
															<span
																className={cn(
																	"inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200",
																	v ? 'translate-x-5' : 'translate-x-1'
																)}
															/>
														</button>
													);
												}
												if (col.cellRenderer === 'badge' && CELL_RENDERERS.badge) {
													return CELL_RENDERERS.badge(v, col, row, theme);
												}
												if (col.cellRenderer === 'statusBadge' && CELL_RENDERERS.statusBadge) {
													return CELL_RENDERERS.statusBadge(v, col, row, theme);
												}
												if (col.cellRenderer === 'fileCount') {
													return (
														<div className="flex justify-center items-center gap-1">
															<FileIcon className="w-4 h-4 text-blue-500" />
															<span 
																className={`text-sm font-medium ${theme.text.primary} cursor-pointer hover:text-blue-600`} 
																title={`${v || 0} file(s) - Click to view/download`}
																onClick={() => {
																	console.log('📁 File count clicked in CardTable:', { value: v, row, hasHandler: !!onFileCountClick });
																	if (onFileCountClick) {
																		onFileCountClick(row, v || 0);
																	} else {
																		console.log('❌ No onFileCountClick handler provided to CardTable');
																	}
																}}
															>
																{v || 0}
															</span>
														</div>
													);
												}
												return String(v);
											})()}
										</div>
									</div>
								))}
						</div>

						{/* Actions footer */}
						{actionsRenderer && (
							<div className={cn("px-4 pb-3 pt-0")}
								onClick={(e) => e.stopPropagation()}
							>
								<div className={cn(
									"flex items-center justify-end",
									spacing,
									actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
								)}>
									{actionsRenderer(row)}
								</div>
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

export default CardTable;
export { CardTable };


